import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'
import { cn } from '@/lib/utils'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronDown,
  Folder,
  GripVertical,
  Link,
  Pencil,
  Plus,
  ShoppingBag,
  Tags,
  Trash2,
} from 'lucide-react'
import { useRef, useState } from 'react'
import CollectionStorefrontVisibilityIndicator from './CollectionStorefrontVisibilityIndicator'
import {
  COLLECTION_STOREFRONT_VISIBILITY_ISSUE,
  getCollectionStorefrontVisibilityIssue,
} from './designForm.utils'
import {
  getNavigationItem,
  NAVIGATION_ITEM_TYPE,
  navigationItemLabel,
  reorderNavigationItems,
} from './navigationMenuEditor.utils'

const typeDetails = {
  [NAVIGATION_ITEM_TYPE.COLLECTION]: { label: 'Collection', icon: Folder },
  [NAVIGATION_ITEM_TYPE.LINK]: { label: 'Custom link', icon: Link },
  [NAVIGATION_ITEM_TYPE.MENU]: { label: 'Dropdown menu', icon: ChevronDown },
  [NAVIGATION_ITEM_TYPE.SHOP_ALL]: { label: 'Shop All', icon: ShoppingBag },
  [NAVIGATION_ITEM_TYPE.OCCASIONS]: { label: 'Occasions', icon: Tags },
}

const navigationAutoScrollOptions = {
  acceleration: 5,
  threshold: { x: 0.2, y: 0.08 },
}

function sameNavigationParent(leftParentId, rightParentId) {
  return String(leftParentId ?? '') === String(rightParentId ?? '')
}

export function navigationCollisionDetection(args, lastPointerCollisionId = null) {
  const activeParentId = args.active.data.current?.parentId ?? null
  const droppableContainers = args.droppableContainers.filter((container) =>
    sameNavigationParent(container.data.current?.parentId, activeParentId)
  )
  const collisionArgs = { ...args, droppableContainers }
  const pointerCollisions = pointerWithin(collisionArgs)

  if (pointerCollisions.length) return pointerCollisions
  if (!args.pointerCoordinates) return closestCenter(collisionArgs)

  const lastPointerContainer = droppableContainers.find(
    (container) => String(container.id) === String(lastPointerCollisionId)
  )
  if (lastPointerContainer) {
    return [
      {
        id: lastPointerContainer.id,
        data: { droppableContainer: lastPointerContainer, value: 0 },
      },
    ]
  }

  const { x, y } = args.pointerCoordinates
  return closestCenter({
    ...collisionArgs,
    collisionRect: {
      ...args.collisionRect,
      top: y,
      right: x,
      bottom: y,
      left: x,
      width: 0,
      height: 0,
    },
  })
}

function NavigationMenuDragPreview({ item, collectionsById, width }) {
  const details = typeDetails[item.type] ?? typeDetails[NAVIGATION_ITEM_TYPE.LINK]
  const Icon = details.icon
  const childCount = item.type === NAVIGATION_ITEM_TYPE.MENU ? (item.children?.length ?? 0) : null

  return (
    <div
      className="flex items-center gap-3 rounded-xl border bg-card p-3 shadow-none hover:shadow-sm"
      style={width ? { width } : undefined}
      data-navigation-drag-preview=""
    >
      <span className="flex size-8 shrink-0 items-center justify-center text-muted-foreground">
        <GripVertical className="size-4" />
      </span>
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accentSecondary text-accentSecondary-foreground">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {navigationItemLabel(item, collectionsById)}
        </div>
        <div className="text-xs text-muted-foreground">
          {details.label}
          {childCount !== null ? ` · ${childCount} ${childCount === 1 ? 'link' : 'links'}` : null}
        </div>
      </div>
    </div>
  )
}

function SortableMenuItem({
  item,
  parentId,
  collectionsById,
  onEdit,
  onRemove,
  onAddChild,
  allowItemChanges,
  getItemClassName,
  renderItemEnd,
  renderItemMeta,
  showCollectionVisibilityIndicator,
  visibleItemIds,
}) {
  const { isReadOnly } = useIsReadOnly()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: isReadOnly,
    data: { parentId },
  })
  const details = typeDetails[item.type] ?? typeDetails[NAVIGATION_ITEM_TYPE.LINK]
  const Icon = details.icon
  const collection = collectionsById.get(Number(item.collection_id))
  const collectionVisibilityIssue =
    item.type === NAVIGATION_ITEM_TYPE.COLLECTION
      ? collection
        ? getCollectionStorefrontVisibilityIssue(collection)
        : COLLECTION_STOREFRONT_VISIBILITY_ISSUE.HIDDEN
      : null
  const destination = item.type === NAVIGATION_ITEM_TYPE.LINK ? item.href : null
  const style = {
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  }
  const itemContext = { item, collection, details, parentId }
  const visibleChildren = visibleItemIds
    ? (item.children ?? []).filter((child) => visibleItemIds.has(String(child.id)))
    : (item.children ?? [])
  const hiddenChildCount = (item.children?.length ?? 0) - visibleChildren.length

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-xl border bg-card shadow-xs',
        getItemClassName?.(itemContext),
        isDragging && 'opacity-0'
      )}
      data-navigation-item={item.id}
    >
      <div className="flex min-w-0 items-center gap-3 p-3">
        <Button
          variant="ghostMuted"
          size="icon-xs"
          className="cursor-grab touch-none active:cursor-grabbing"
          aria-label={`Reorder ${navigationItemLabel(item, collectionsById)}`}
          disabled={isReadOnly}
          {...attributes}
          {...listeners}
        >
          <GripVertical />
        </Button>

        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accentSecondary text-accentSecondary-foreground">
          <Icon className="size-4" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-sm font-medium">
              {navigationItemLabel(item, collectionsById)}
            </span>
            {showCollectionVisibilityIndicator && collectionVisibilityIssue ? (
              <CollectionStorefrontVisibilityIndicator
                visibilityIssue={collectionVisibilityIssue}
              />
            ) : null}
          </div>
          {renderItemMeta ? (
            renderItemMeta(itemContext)
          ) : (
            <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
              <span>{details.label}</span>
              {destination ? <span className="truncate">· {destination}</span> : null}
            </div>
          )}
        </div>

        {allowItemChanges ? (
          <div
            className="flex shrink-0 flex-col items-end gap-1 @md:flex-row @md:items-center"
            data-navigation-row-actions=""
          >
            {renderItemEnd?.(itemContext)}
            {!isReadOnly ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label={`Edit ${navigationItemLabel(item, collectionsById)}`}
                  onClick={() => onEdit(item.id, parentId)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghostDestructive"
                  size="icon-xs"
                  aria-label={`Remove ${navigationItemLabel(item, collectionsById)}`}
                  onClick={() => onRemove(item)}
                >
                  <Trash2 />
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          renderItemEnd?.(itemContext)
        )}
      </div>

      {item.type === NAVIGATION_ITEM_TYPE.MENU ? (
        <div className="border-t bg-muted/25 px-3 py-3 pl-12">
          {visibleChildren.length ? (
            <SortableContext
              items={visibleChildren.map((child) => child.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-2">
                {visibleChildren.map((child) => (
                  <SortableMenuItem
                    key={child.id}
                    item={child}
                    parentId={item.id}
                    collectionsById={collectionsById}
                    onEdit={onEdit}
                    onRemove={onRemove}
                    onAddChild={onAddChild}
                    allowItemChanges={allowItemChanges}
                    getItemClassName={getItemClassName}
                    renderItemEnd={renderItemEnd}
                    renderItemMeta={renderItemMeta}
                    showCollectionVisibilityIndicator={showCollectionVisibilityIndicator}
                    visibleItemIds={visibleItemIds}
                  />
                ))}
              </ul>
            </SortableContext>
          ) : hiddenChildCount ? (
            <Typography variant="supporting" className="block">
              {hiddenChildCount} hidden {hiddenChildCount === 1 ? 'link' : 'links'}
            </Typography>
          ) : (
            <Typography variant="supporting" className="block">
              This dropdown is empty. Add at least one link before saving.
            </Typography>
          )}

          {!isReadOnly && allowItemChanges ? (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => onAddChild(item.id)}>
              <Plus /> Add link
            </Button>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

export default function NavigationMenuList({
  items,
  collectionsById,
  onEdit,
  onRemove,
  onAddChild,
  onReorder,
  allowItemChanges = true,
  getItemClassName,
  renderItemEnd,
  renderItemMeta,
  showCollectionVisibilityIndicator = true,
  visibleItemIds = null,
}) {
  const [activeDrag, setActiveDrag] = useState(null)
  const lastPointerCollisionId = useRef(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragStart = ({ active }) => {
    lastPointerCollisionId.current = null
    const location = getNavigationItem(items, active.id)
    if (!location) return

    setActiveDrag({
      item: location.item,
      width: active.rect.current.initial?.width ?? null,
    })
  }

  const handleDragEnd = ({ active, over }) => {
    setActiveDrag(null)
    lastPointerCollisionId.current = null
    if (!over || String(active.id) === String(over.id)) return

    const activeLocation = getNavigationItem(items, active.id)
    const overLocation = getNavigationItem(items, over.id)
    if (!activeLocation || !overLocation) return
    if (String(activeLocation.parentId) !== String(overLocation.parentId)) return

    onReorder(
      reorderNavigationItems(items, active.id, over.id, activeLocation.parentId, visibleItemIds)
    )
  }

  const detectNavigationCollision = (args) => {
    const collisions = navigationCollisionDetection(args, lastPointerCollisionId.current)
    if (args.pointerCoordinates && collisions.length) {
      lastPointerCollisionId.current = collisions[0].id
    }
    return collisions
  }

  const visibleItems = visibleItemIds
    ? items.filter((item) => visibleItemIds.has(String(item.id)))
    : items

  return (
    <DndContext
      sensors={sensors}
      autoScroll={navigationAutoScrollOptions}
      collisionDetection={detectNavigationCollision}
      onDragStart={handleDragStart}
      onDragCancel={() => {
        setActiveDrag(null)
        lastPointerCollisionId.current = null
      }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={visibleItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="space-y-2">
          {visibleItems.map((item) => (
            <SortableMenuItem
              key={item.id}
              item={item}
              parentId={null}
              collectionsById={collectionsById}
              onEdit={onEdit}
              onRemove={onRemove}
              onAddChild={onAddChild}
              allowItemChanges={allowItemChanges}
              getItemClassName={getItemClassName}
              renderItemEnd={renderItemEnd}
              renderItemMeta={renderItemMeta}
              showCollectionVisibilityIndicator={showCollectionVisibilityIndicator}
              visibleItemIds={visibleItemIds}
            />
          ))}
        </ul>
      </SortableContext>
      <DragOverlay adjustScale={false} dropAnimation={null} zIndex={100}>
        {activeDrag ? (
          <NavigationMenuDragPreview
            item={activeDrag.item}
            collectionsById={collectionsById}
            width={activeDrag.width}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
